/**
 * useLocalizedRouteCatalog — localize the typed route catalog
 *
 * The typed `fmRouteCatalogDefault` carries English defaults as config. The nav
 * surfaces (FmTopNav mega-menu + FmMobileNavDrawer) rendered those defaults
 * verbatim, so job labels, group titles/descriptions, route labels/descriptions,
 * and promo-card copy leaked English into the FI (and other) locales.
 *
 * This hook maps the catalog through `t.shell.nav.catalog` (keyed by catalog id)
 * so every rendered nav string resolves to the active locale. The English
 * default stays as a defensive fallback; the locale-completeness gate guarantees
 * every locale carries the keys, so the fallback should never fire in practice.
 *
 * Paths, ids, and active-state resolution are untouched — only display copy is
 * localized.
 */
import { useMemo } from 'react';
import { fmRouteCatalogDefault } from './FmRouteCatalogDefault';
import type {
  FmNavGroupEntry,
  FmNavPromoCardEntry,
  FmRouteCatalogData,
  FmRouteCatalogEntry,
  FmTopNavJobEntry,
} from './FmRouteCatalog';
import { useLanguage } from '@/i18n/LanguageContext';
import type { ShellNavCatalogTranslations } from '@/i18n/types';

function localizedRoute(
  route: FmRouteCatalogEntry,
  catalog: ShellNavCatalogTranslations,
): FmRouteCatalogEntry {
  const localized = catalog.routes[route.id];
  const label = localized?.label ?? route.label;
  const description = localized?.description ?? route.description;
  if (route.placement === 'menu' && route.selection) {
    return {
      ...route,
      label,
      description,
      selection: localized?.selection ?? route.selection,
    };
  }
  return {
    ...route,
    label,
    description,
  };
}

function localizedGroup(
  group: FmNavGroupEntry,
  catalog: ShellNavCatalogTranslations,
): FmNavGroupEntry {
  const localized = catalog.groups[group.id];
  return {
    ...group,
    label: localized?.label ?? group.label,
    description: localized?.description ?? group.description,
  };
}

function localizedJob(
  job: FmTopNavJobEntry,
  catalog: ShellNavCatalogTranslations,
): FmTopNavJobEntry {
  return { ...job, label: catalog.jobs[job.id] ?? job.label };
}

function localizedPromo(
  card: FmNavPromoCardEntry,
  catalog: ShellNavCatalogTranslations,
): FmNavPromoCardEntry {
  const localized = catalog.promos[card.id];
  return {
    ...card,
    heading: localized?.heading ?? card.heading,
    body: localized?.body ?? card.body,
    action: {
      ...card.action,
      label: localized?.action ?? card.action.label,
    },
  };
}

export function useLocalizedRouteCatalog(
  base: FmRouteCatalogData = fmRouteCatalogDefault,
): FmRouteCatalogData {
  const { t } = useLanguage();
  const catalog = t.shell.nav.catalog;

  return useMemo<FmRouteCatalogData>(
    () => ({
      routes: base.routes.map((route) => localizedRoute(route, catalog)),
      groups: base.groups.map((group) => localizedGroup(group, catalog)),
      topJobs: base.topJobs.map((job) => localizedJob(job, catalog)),
      promoCards: base.promoCards.map((card) => localizedPromo(card, catalog)),
    }),
    [base, catalog],
  );
}
